// 23章 植栽工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_SOIL_LAYER = `
<svg viewBox="0 0 660 230" role="img" aria-label="樹木等に応じた有効土層の厚さ。有効土層とは植物の根が支障なく伸びられる土の層。高木（樹高12m以上）は100cm、高木（7〜12m未満）は80cm、低木（3〜7m未満）は60cm、低木（3m未満）は50cm、芝・地被類は30cmが標準。木が大きいほど根を張るため厚い土層が必要">
  <g fill="currentColor" opacity="0.15"><rect x="40" y="150" width="580" height="60"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5"><line x1="40" y1="150" x2="620" y2="150"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.4">
    <rect x="60" y="60" width="70" height="90"/>
    <rect x="170" y="82" width="70" height="68"/>
    <rect x="280" y="98" width="70" height="52"/>
    <rect x="390" y="108" width="70" height="42"/>
    <rect x="500" y="126" width="70" height="24"/>
  </g>
  <g fill="currentColor" font-size="14" font-weight="bold" text-anchor="middle">
    <text x="95" y="128">100</text>
    <text x="205" y="128">80</text>
    <text x="315" y="132">60</text>
    <text x="425" y="135">50</text>
    <text x="535" y="143">30</text>
  </g>
  <g fill="currentColor" font-size="11" text-anchor="middle">
    <text x="95" y="172">高木</text><text x="95" y="186" opacity="0.8">12m以上</text>
    <text x="205" y="172">高木</text><text x="205" y="186" opacity="0.8">7〜12m</text>
    <text x="315" y="172">低木</text><text x="315" y="186" opacity="0.8">3〜7m</text>
    <text x="425" y="172">低木</text><text x="425" y="186" opacity="0.8">3m未満</text>
    <text x="535" y="172">芝・地被</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="40" font-weight="bold">有効土層の厚さ（cm）（表23.2.1）</text>
    <text x="40" y="222">有効土層＝根が支障なく伸びる土層。木が大きいほど厚い土層が必要</text>
  </g>
</svg>`;

const SVG_TREE_PLANT = `
<svg viewBox="0 0 660 240" role="img" aria-label="樹木の植付けと支柱。樹木に応じた植穴を掘り、穴底に用土を敷いて根鉢を入れ、根回りに用土を入れて水ぎめ・土ぎめをする。植付け後、幹の周りに土手状の水鉢を設けて水を溜める。八ッ掛け形などの支柱を丸太で組み、幹との取付け部は杉皮を巻いてしゅろ縄で結束する。幹は幹巻きテープで覆って保護する">
  <g fill="currentColor" opacity="0.12"><path d="M40 150 h580 v90 h-580 z"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M255 150 q-40 8 -50 40 q0 30 95 30 q95 0 95 -30 q-10 -32 -50 -40"/>
    <line x1="300" y1="60" x2="300" y2="185"/>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="300" cy="55" r="34"/><circle cx="272" cy="72" r="20"/><circle cx="328" cy="72" r="20"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.4"><ellipse cx="300" cy="180" rx="38" ry="16"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5"><line x1="230" y1="205" x2="300" y2="120"/><line x1="370" y1="205" x2="300" y2="120"/></g>
  <g fill="currentColor" opacity="0.85"><circle cx="300" cy="120" r="4"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5"><path d="M255 195 q45 -18 90 0" /></g>
  <g fill="currentColor" font-size="12">
    <text x="360" y="185">根鉢</text>
    <text x="345" y="205" opacity="0.85">水鉢</text>
    <text x="380" y="150">八ッ掛け支柱（丸太）</text>
    <text x="305" y="118">杉皮＋しゅろ縄で結束</text>
    <text x="150" y="90">幹巻き</text>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="1.2" stroke-dasharray="3 2"><path d="M295 70 v80"/></g>
  <g fill="currentColor" font-size="12" opacity="0.8"><text x="40" y="30" font-weight="bold">樹木の植付けと支柱（23.3.3）</text></g>
</svg>`;

const SVG_SOD = `
<svg viewBox="0 0 660 210" role="img" aria-label="芝張りの工法。目地張りは平地に用い、目地寸法30mm以内で切芝を置き、横目地を通し縦目地は芋目地にならないようずらす。べた張りは法面に用い、目地なしにすき間なく張り付け、4本以上の芝ぐしで留め付ける。いずれも張り後にローラー等で転圧して芝の根を土壌に密着させる">
  <g fill="none" stroke="currentColor" stroke-width="1.5"><rect x="30" y="55" width="290" height="130" rx="6"/><rect x="340" y="55" width="290" height="130" rx="6"/></g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="175" y="45">目地張り（平地）</text>
    <text x="485" y="45">べた張り（法面）</text>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.7">
    <rect x="55" y="80" width="55" height="35"/><rect x="122" y="80" width="55" height="35"/><rect x="189" y="80" width="55" height="35"/>
    <rect x="88" y="128" width="55" height="35"/><rect x="155" y="128" width="55" height="35"/><rect x="222" y="128" width="55" height="35"/>
  </g>
  <g fill="currentColor" opacity="0.35"><rect x="360" y="80" width="250" height="83"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1" opacity="0.6"><path d="M360 121 h250 M485 80 v83"/></g>
  <g fill="currentColor" font-size="11.5">
    <text x="50" y="178" opacity="0.85">目地30mm以内・縦目地はずらす</text>
    <text x="360" y="178" opacity="0.85">目地なし・芝ぐし4本以上で留付け</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8"><text x="30" y="205">張り後に転圧し芝の根を土壌に密着させる（23.4.3）</text></g>
</svg>`;

const SVG_ROOFTOP = `
<svg viewBox="0 0 660 240" role="img" aria-label="屋上緑化システムの層構成。防水層のある屋上の上に、下から耐根層（根の貫通を防ぐ）、耐根層保護層、排水層（余分な水を排出）、透水層（水は通すが土は流さない）、土壌層（植込み用土）を重ね、いちばん上に植物を植える。植栽基盤の質量は軽量システムで60kg毎平方メートル以下">
  <g fill="currentColor" opacity="0.5"><rect x="90" y="200" width="480" height="20"/></g>
  <g fill="currentColor" opacity="0.4"><rect x="90" y="188" width="480" height="12"/></g>
  <g fill="currentColor" opacity="0.3"><rect x="90" y="170" width="480" height="18"/></g>
  <g fill="currentColor" opacity="0.2"><rect x="90" y="152" width="480" height="18"/></g>
  <g fill="currentColor" opacity="0.12"><rect x="90" y="110" width="480" height="42"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.4"><rect x="90" y="110" width="480" height="110"/><line x1="90" y1="152" x2="570" y2="152"/><line x1="90" y1="170" x2="570" y2="170"/><line x1="90" y1="188" x2="570" y2="188"/><line x1="90" y1="200" x2="570" y2="200"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.4">
    <path d="M180 110 q-14 -26 0 -46 q14 20 0 46" fill="none"/>
    <line x1="300" y1="110" x2="300" y2="72"/><circle cx="300" cy="66" r="10"/>
    <path d="M420 110 v-30 M420 96 l-8 -8 M420 96 l8 -8"/>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="end">
    <text x="565" y="130" opacity="0.85">土壌層（植込み用土）</text>
    <text x="565" y="165" opacity="0.85">透水層</text>
    <text x="565" y="183" opacity="0.85">排水層</text>
    <text x="565" y="197" opacity="0.85">耐根層保護層</text>
    <text x="565" y="214" opacity="0.85">耐根層（根の貫通防止）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="90" y="40" font-weight="bold">屋上緑化システムの層構成（23.5.2）</text>
    <text x="90" y="58">防水層のある屋上に各層を重ねる。軽量システムは基盤の質量60kg/㎡以下</text>
  </g>
</svg>`;

const ch23: Record<string, Commentary> = {
  "23.1.1": {
    plainSummary:
      "この章は、樹木・芝張り・吹付けは種・地被類の植栽工事、及び屋上緑化工事に適用する。1章とセットで適用する。",
    why: "植栽は「生き物」を扱う唯一の工事です。他の工事が完成した時点で品質が確定するのに対し、植栽は植えてから「活着（根付いて育つ）」するかどうかが問われます。そのため枯れた場合の補償（枯補償）まで規定されているのが特徴です。",
    glossary: [
      {
        term: "地被類（ちひるい）",
        meaning:
          "地面を覆うように低く広がる植物（グラウンドカバー）。芝以外のリュウノヒゲ・ツタ類など。",
      },
    ],
  },

  "23.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②樹木・支柱等が所定の形状・寸法で、植物が所定位置に形姿良く有害な傷なく植えられている ③新植の樹木等が活着するよう育成したものであること。",
    why: "「活着するよう育成したもの」という要求が植栽ならでは。ただ植えるのではなく、根付いて生き続けられるように、根回し栽培品を使い、適切な時期・方法で植え、養生することが求められます。",
    glossary: [
      {
        term: "活着（かっちゃく）",
        meaning:
          "植えた樹木が新しい場所で根付き、生育を始めること。植栽工事の成否はこれで決まる。",
      },
    ],
  },

  "23.1.3": {
    plainSummary:
      "植栽地の排水性（透水性）・土壌硬度が植栽に適していることを確認する（pH・電気伝導度等の試験は特記）。結果の記録を監督職員に提出し、生育に支障のおそれがあれば協議する。",
    why: "植える前の「土のチェック」が肝心です。水はけが悪い、土が硬すぎる、酸性・アルカリ性が強すぎる土では、どんな良い苗も枯れます。植栽の失敗の多くは土壌が原因なので、事前確認が重視されます。",
    points: ["植える前に排水性・土壌硬度・pHを確認する"],
  },

  "23.2.1": {
    plainSummary:
      "この節は、植栽地を植物が正常に生育できる状態に整備する植栽基盤の整備に適用する。「有効土層」とは、植物の根が支障なく伸びられるよう整備する土層をいう。",
    glossary: [
      {
        term: "有効土層",
        meaning:
          "植物の根が支障なく伸びられる土の層。木が大きいほど厚い有効土層が必要になる。",
      },
    ],
  },

  "23.2.2": {
    plainSummary:
      "植栽基盤の整備の適用は特記（芝・地被類は必ず整備）。有効土層の面積・厚さは特記（なければ表23.2.1：高木100/80cm・低木60/50cm・芝地被30cm）。排水設備の設置は特記。整備工法は表23.2.2（A〜D種、なければ樹木はA種・芝地被はB種）。土壌改良材の適用は特記。",
    why: "木の大きさに応じて必要な土の厚さ（有効土層）が決まります。高木は深く根を張るので100cm、芝は30cmで足りる、という具合です。ここが浅いと根が伸びず、木が育たない・倒れる原因になります。",
    points: [
      "有効土層は樹高に応じて（高木100cm〜芝地被30cm）",
      "整備工法は樹木A種・芝地被B種が標準",
    ],
    diagrams: [
      {
        svg: SVG_SOIL_LAYER,
        caption: "樹木等に応じた有効土層の厚さ（表23.2.1）",
      },
    ],
  },

  "23.2.3": {
    plainSummary:
      "材料。植込み用土は客土または現場発生土の良質土（適用は特記）。客土は生育に適し、小石・ごみ・雑草等のきょう雑物を含まない良質土。土壌改良材の種類は特記、使用時は土壌との適合性を確認し品質証明資料を提出して承諾を受ける。",
    glossary: [
      {
        term: "客土（きゃくど）",
        meaning:
          "他所から運び入れる良質な土。現場の土が植栽に適さない場合に入れ替える。",
      },
    ],
  },

  "23.2.4": {
    plainSummary:
      "工法。A種は粗起し→耕うん（20cm程度を細砕し、雨水が浸透できる程度に軽く締固め）。B種は有効土層を耕うん。C種は既存土を除去し植込み用土を敷均し。D種は現状地盤の上に植込み用土を盛土。土壌改良材は指定量を適切にかくはん。必要に応じ施肥。",
    why: "土を「雨水が浸透できる程度に軽く締め固める」のがポイント。舗装と違い、植栽基盤は締めすぎると根が伸びず水も通りません。かといって緩すぎても沈下します。ちょうどよい柔らかさに耕すのが植栽基盤づくりの要です。",
    points: [
      "耕うんで土をほぐし、軽く締める（締めすぎない）",
      "A種は粗起し＋耕うん、B種は耕うんのみ",
    ],
    glossary: [
      {
        term: "耕うん",
        meaning:
          "土を細かく砕いてほぐすこと。硬く締まった土を、根が伸びやすく水が浸透しやすい状態にする。",
      },
    ],
  },

  "23.3.1": {
    plainSummary: "この節は、樹木の新植及び移植工事に適用する。",
  },

  "23.3.2": {
    plainSummary:
      "材料。樹木は発育良好で枝葉が密生し病虫害のない樹姿の良いもので、根回しまたはコンテナ栽培をした細根の多い栽培品。樹種・寸法・株立数・数量は特記（寸法は搬入時点を最小とする）。樹高・枝張り・幹周・根元周・株立の測定方法が定められる。支柱材は丸太（杉・ひのき・から松の皮はぎ、加圧式防腐処理）または竹。幹巻き用材料は幹巻きテープ・わら・こも。",
    why: "「根回しをした栽培品」を使うのが重要。根回しとは、移植の前もって根を切って細かい根（細根）を発生させておく作業です。細根が多いほど水を吸う力が強く、移植後に活着しやすくなります。幹周を1.2mの高さで測るなど、樹木の寸法の測り方も規格で定められています。",
    points: [
      "根回し・コンテナ栽培の細根の多い栽培品を使う",
      "幹周は根鉢上端から1.2mの高さで測定",
    ],
    glossary: [
      {
        term: "根回し（ねまわし）",
        meaning:
          "移植の前に根を一部切っておき、細かい根を発生させる作業。活着率を高める。",
      },
      {
        term: "根鉢（ねばち）",
        meaning:
          "掘り取った樹木の根と、それを包む土のかたまり。これを崩さず植えると活着しやすい。",
      },
    ],
  },

  "23.3.3": {
    plainSummary:
      "新植の工法。根を保護して搬入し、搬入後直ちに植付け（やむを得ない場合は仮植え・養生）。植穴を掘り穴底に用土を敷いて根鉢を入れ、根回りに用土を入れて水ぎめ／土ぎめし地均し、植付け後に水鉢を設ける。支柱は添え柱形・鳥居形・八ッ掛け形等（特記）、基部を埋め根杭で固定、幹との取付け部は杉皮を巻きしゅろ縄で結束。幹巻きで樹幹・主枝を覆う。整姿せん定・かん水等の養生を行う。",
    why: "植付けの核心は「水ぎめ」と「水鉢」です。水ぎめは、用土を入れながら水を注いで泥状にし、根と土の間の空気を追い出して密着させる方法。空気が残ると根が乾いて枯れます。水鉢は幹の周りに作る土手で、かん水した水を逃がさず根に浸透させる工夫です。支柱は根付くまで木が風で揺れて根が切れるのを防ぎます。",
    points: [
      "水ぎめで根と土を密着させる（空気を追い出す）",
      "植付け後に水鉢を設けて水を溜める",
      "支柱で幹を固定し、根の活着を助ける",
    ],
    glossary: [
      {
        term: "水ぎめ",
        meaning:
          "植穴に用土と水を入れ、泥状にして根の周りの空気を抜き土を密着させる植付け方法。",
      },
      {
        term: "水鉢（みずばち）",
        meaning:
          "植えた木の根元の周りに作る土手状のくぼみ。かん水した水を溜めて根に浸透させる。",
      },
    ],
    diagrams: [
      {
        svg: SVG_TREE_PLANT,
        caption: "樹木の植付けと支柱（23.3.3）",
      },
    ],
  },

  "23.3.4": {
    plainSummary:
      "新植樹木の枯補償。期間は特記（なければ引渡しの日から1年）。期間内に枯死・枝損傷・形姿不良等となった場合は同等以上のものを再植樹し、取り除いた樹木を処分する（天災等やむを得ない場合を除く）。",
    why: "植栽独特の「枯補償」制度です。植えた木が1年以内に枯れたら、施工者の責任で植え直します。生き物なので確実に育つ保証はできませんが、活着まで見届ける責任を負わせることで品質を担保しています。",
    points: ["引渡しから1年以内に枯れたら同等以上のものを再植樹"],
  },

  "23.3.5": {
    plainSummary:
      "樹木の移植。掘取りに先立ち枝抜き・摘葉し仮支柱を取り付ける等の養生を行う。根鉢は特性に応じた大きさに掘り出し（太い根はやや長めに切り、細根の密生部は残す）、わら縄・こも等で堅固に根巻き。損傷・鉢崩れ・乾燥のないよう養生後、直ちに移植場所へ運搬。以降は新植（23.3.3）による。",
    why: "移植では、掘る前に枝葉を減らす（枝抜き・摘葉）のがポイント。根を切ると水の吸い上げが減るので、葉からの蒸散も減らしてバランスを取り、木の負担を軽くします。細根を残し根巻きで根鉢を守ることで、移植先での活着を助けます。",
    points: [
      "掘取り前に枝抜き・摘葉し、根と葉のバランスを取る",
      "細根を残し、根鉢を根巻きで保護",
    ],
  },

  "23.3.6": {
    plainSummary:
      "移植樹木の枯損処置。期間は特記（なければ引渡しの日から1年）。期間内に枯死した場合は直ちに伐採・抜根し良質土で埋め戻し整地する。",
    why: "移植木は新植と違い「同等以上のものを再植樹」ではなく「伐採・抜根して整地」までが規定です。既存の大木の移植など、同じものを用意できないケースがあるためです。",
  },

  "23.4.1": {
    plainSummary: "この節は、芝・吹付けは種・地被類の新植に適用する。",
  },

  "23.4.2": {
    plainSummary:
      "材料。芝はコウライシバまたはノシバ（特記なければコウライシバ）、雑草の混入しない土付きの切芝。芝ぐしは竹を割った長さ150mm以上。吹付けは種用種子は特記（なければ洋芝類、発芽率80％以上）、ファイバー（木質繊維）・粘着剤（ポリビニルアルコール等）・肥料。地被類は発育の盛んなコンテナ栽培品。",
    glossary: [
      {
        term: "切芝（きりしば）",
        meaning:
          "土付きで四角く切り出した芝。これを地面に並べて張り、根付かせる。",
      },
      {
        term: "芝ぐし",
        meaning:
          "芝を地面に留めるための竹製の串。法面などで芝がずれないよう打ち込む。",
      },
    ],
  },

  "23.4.3": {
    plainSummary:
      "芝張りの工法。目地張りまたはべた張り（特記なければ平地は目地張り・法面はべた張り）。目地張りは目地30mm以内で切芝を置き、横目地を通し縦目地は芋目地にならないようにし、ハンドローラーで転圧して根を密着。目土を100㎡につき2㎥程度散布。べた張りは目地なしに張り付け、芝ぐし4本以上で留め付ける。張り後は適宜かん水。",
    why: "芝を「芋目地にならないように」（継目を一直線に揃えない）張るのは、目地が通ると雨で土が流され、そこから芝がはがれやすくなるためです。レンガ積みと同じ考え方。目土（めつち）を散布するのは、目地のすき間を埋め、芝の根の乾燥を防いで発根を促すためです。法面ではべた張りで芝ぐし留めし、滑り落ちを防ぎます。",
    points: [
      "平地は目地張り・法面はべた張り",
      "縦目地は芋目地にしない（継目をずらす）",
      "転圧して根を土壌に密着、目土を散布",
    ],
    glossary: [
      {
        term: "目土（めつち）",
        meaning:
          "芝張り後にまく細かい土。目地を埋め、根の乾燥を防いで芝の活着を助ける。",
      },
    ],
    diagrams: [
      {
        svg: SVG_SOD,
        caption: "芝張りの工法（目地張り・べた張り）（23.4.3）",
      },
    ],
  },

  "23.4.4": {
    plainSummary:
      "吹付けは種の工法。種子・ファイバー類・粘着剤・土・肥料を水とかくはんし、種子散布機で所定の位置に吹き付ける。",
    why: "吹付けは種は、法面など人が張りにくい広い斜面を緑化する方法です。種と繊維・粘着剤を混ぜて吹き付けることで、種が雨で流れず斜面に定着し、発芽します。",
    glossary: [
      {
        term: "吹付けは種（ふきつけはしゅ）",
        meaning:
          "種子・繊維・粘着剤・肥料を混ぜて機械で吹き付ける緑化工法。法面など広い斜面に適する。",
      },
    ],
  },

  "23.4.5": {
    plainSummary:
      "地被類の工法。植栽基盤に所定の単位面積当たりの株数を千鳥に植え、軽く押さえてかん水する。つる性植物は中心部から外側へ茎・枝が伸びるように誘引する。",
    why: "地被類を「千鳥」（互い違い）に植えるのは、均一に地面を覆わせ、株の間隔のムラをなくすためです。やがて広がって地面全体を緑で覆います。",
  },

  "23.4.6": {
    plainSummary:
      "養生その他。工事完成直前に雑草等を取り除く。芝・吹付けは種・地被類の施工後、工事期間中は必要に応じて養生し、特に乾燥が著しい場合は適宜かん水する。",
  },

  "23.4.7": {
    plainSummary:
      "芝・吹付けは種・地被類の枯補償。枯損した芝・地被類の処置は23.3.4（新植樹木の枯補償）に準ずる。",
  },

  "23.5.1": {
    plainSummary:
      "この節は、屋上緑化システムまたは屋上緑化軽量システムを用いて、防水層のある屋上に緑化を行う工事に適用する（屋上緑化システムは保護コンクリートがある場合に限る）。",
    why: "屋上緑化は、防水層の上に土をのせて植栽する特殊な工事です。地面の植栽と違い、①根が防水層を突き破らないようにする ②余分な水を確実に排水する ③重すぎて建物に負担をかけない、という3つの課題があり、専用の層構成システムで解決します。",
    glossary: [
      {
        term: "屋上緑化",
        meaning:
          "建物の屋上を植物で緑化すること。断熱・ヒートアイランド緩和・景観向上の効果がある。",
      },
    ],
  },

  "23.5.2": {
    plainSummary:
      "植栽基盤。屋上緑化システムは耐根層・耐根層保護層・排水層・透水層・土壌層で構成（土壌層厚さは特記）。屋上緑化軽量システムも同構成で工法は製造所仕様、植栽基盤の質量は60kg/㎡以下とする。",
    why: "屋上緑化の層構成が肝です。下から①耐根層（根が防水層を貫通するのを防ぐ）②排水層（余分な水を流す）③透水層（水は通すが土は流さない）④土壌層、と役割分担しています。軽量システムで質量60kg/㎡以下に抑えるのは、建物の許容荷重を超えないためです。",
    points: [
      "耐根層で根の防水層貫通を防ぐ",
      "排水層・透水層で水を管理する",
      "軽量システムは基盤の質量60kg/㎡以下",
    ],
    glossary: [
      {
        term: "耐根層（たいこんそう）",
        meaning:
          "植物の根が下の防水層を突き破るのを防ぐ層。屋上緑化で漏水を防ぐ最重要の層。",
      },
    ],
    diagrams: [
      {
        svg: SVG_ROOFTOP,
        caption: "屋上緑化システムの層構成（23.5.2）",
      },
    ],
  },

  "23.5.3": {
    plainSummary:
      "材料。屋上緑化システムの各層は、耐根層（長期2年以上クマザサ等の地下茎貫通を防止、耐腐食・耐久性）、耐根層保護層、排水層（軽量骨材＋透水排水管、または板状成形品で鉛直240L/㎡・h以上）、透水層（土を流出させない構造）、土壌層（人工軽量土または改良土、飽和透水係数10⁻⁵m/s以上・pH4.5〜7.5）で構成。軽量システムも準ずる。樹木・芝・地被類は23.3・23.4による。見切り材・排水孔・マルチング材等は特記。",
  },

  "23.5.4": {
    plainSummary:
      "工法。建築基準法に基づく風圧力に対応した工法は特記。排水孔・ルーフドレンには目詰まり・土壌流出防止用カバーを設ける。耐根層の水抜き管貫通部回りはシーリング材等で措置。支柱の設置・形式、かん水装置の設置・種類は特記。",
    why: "屋上は風が強く、土や植物が飛ばされたり、根が浅く木が倒れやすい環境です。風圧力への対応（支柱・固定）が重要。またルーフドレン（屋上の排水口）が土で詰まると水が溜まって漏水・荷重増につながるため、土壌流出防止カバーが必須です。",
    points: [
      "風圧力に対応した固定・支柱",
      "排水孔・ルーフドレンに土壌流出防止カバー",
    ],
  },

  "23.5.5": {
    plainSummary:
      "新植樹木・芝・地被類の枯補償。新植樹木は23.3.4、芝・地被類は23.4.7による。",
  },
};

export default ch23;
